import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { Switch } from "design-system/atoms/Switch"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "design-system/molecules/Command"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { ChevronDown } from "lucide-react"
import { Loading } from "modules/shared/components/Loading"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CapabilitiesResponseDataSchema,
  CapabilityCampaignAssignation,
  CapabilityIdentifier,
  CapabilityResponseDataSchema,
  LocationStrategy,
  StockMode,
} from "schema"
import { toast } from "sonner"
import z from "zod"
import { useCapabilityProvider } from "../../hooks/useCapabilityProvider"
import { useInstallCapabilityMutation } from "../../hooks/useInstallCapabilityMutation"
import { useShopifyLocationsQuery } from "../../hooks/useShopifyLocationsQuery"
import { useUpdateCapabilityConfigMutation } from "../../hooks/useUpdateCapabilityConfigMutation"

type Props = {
  capability: z.infer<typeof CapabilitiesResponseDataSchema>[number]
  mode?: "install" | "update"
  initialConfig?: {
    provider?: string
    stockConfig?: {
      stockMode?: string
      locationStrategy?: string
      specificLocationIds?: string[]
      allowBackorder?: boolean
      minQuantity?: number
    }
  }
  onClose: () => void
  onPendingChange?: (isPending: boolean) => void
  onConfigure?: (capability: z.infer<typeof CapabilityResponseDataSchema>) => void
  onSubmitHandlerRef?: React.MutableRefObject<
    | ((
        campaignAssignation: CapabilityCampaignAssignation,
        campaignIds: string[],
        campaignFlow?: "Inbound" | "Outbound",
      ) => Promise<void>)
    | null
  >
}

export function ProductInformationForm({
  capability,
  mode = "install",
  initialConfig,
  onClose,
  onPendingChange,
  onConfigure,
  onSubmitHandlerRef,
}: Props) {
  const { t } = useTranslation()
  const { mutateAsync: installCapability, isPending: isInstallPending } =
    useInstallCapabilityMutation()
  const { mutateAsync: updateCapabilityConfig, isPending: isUpdatePending } =
    useUpdateCapabilityConfigMutation()

  const isPending = mode === "install" ? isInstallPending : isUpdatePending
  const { provider, setProvider, providerOptions, isIntegrationsStatusPending } =
    useCapabilityProvider({
      capability,
      mode,
      initialConfig,
    })

  const [stockMode, setStockMode] = useState(initialConfig?.stockConfig?.stockMode ?? "ignore")
  const [locationStrategy, setLocationStrategy] = useState(
    initialConfig?.stockConfig?.locationStrategy ?? "online_only",
  )
  const [specificLocationIds, setSpecificLocationIds] = useState<string[]>(
    initialConfig?.stockConfig?.specificLocationIds ?? [],
  )
  const [allowBackorder, setAllowBackorder] = useState(
    initialConfig?.stockConfig?.allowBackorder ?? true,
  )
  const [minQuantity, setMinQuantity] = useState(
    initialConfig?.stockConfig?.minQuantity?.toString() ?? "",
  )

  const { data: locations = [], isLoading: isLoadingLocations } = useShopifyLocationsQuery(
    locationStrategy === "specific",
  )

  const buildStockConfig = useCallback(() => {
    if (stockMode === "ignore") return undefined
    const parsedMinQuantity = parseInt(minQuantity, 10)
    return {
      stockMode,
      locationStrategy,
      specificLocationIds: locationStrategy === "specific" ? specificLocationIds : undefined,
      allowBackorder,
      ...(parsedMinQuantity >= 1 && { minQuantity: parsedMinQuantity }),
    }
  }, [stockMode, locationStrategy, specificLocationIds, allowBackorder, minQuantity])

  const toggleLocation = useCallback((locationId: string) => {
    setSpecificLocationIds((prev) =>
      prev.includes(locationId) ? prev.filter((id) => id !== locationId) : [...prev, locationId],
    )
  }, [])

  const locationsTriggerLabel = useMemo(() => {
    if (specificLocationIds.length === 0) {
      return t(
        "capabilities.catalog.ProductInformation.config.stockConfig.specificLocationsPlaceholder",
      )
    }
    return t(
      "capabilities.catalog.ProductInformation.config.stockConfig.specificLocationsSelected",
      {
        count: specificLocationIds.length,
      },
    )
  }, [specificLocationIds.length, t])

  useEffect(() => {
    onPendingChange?.(isPending || isIntegrationsStatusPending)
  }, [isPending, isIntegrationsStatusPending, onPendingChange])

  const handleSubmit = useCallback(
    async (
      campaignAssignation: CapabilityCampaignAssignation,
      campaignIds: string[],
      campaignFlow?: "Inbound" | "Outbound",
    ) => {
      if (isPending) return

      const stockConfigPayload = buildStockConfig()
      let installedCapability: z.infer<typeof CapabilityResponseDataSchema> | undefined

      if (mode === "install") {
        installedCapability = await installCapability({
          identifier: CapabilityIdentifier.ProductInformation,
          config: { provider, stockConfig: stockConfigPayload },
          campaignAssignation,
          campaignIds,
          campaignFlow,
        })

        toast.success(
          t("capabilities.list.install.success", {
            capability: t(`capabilities.catalog.${capability.identifier}.name`),
          }),
        )
      } else {
        await updateCapabilityConfig({
          id: capability.id,
          config: { provider, stockConfig: stockConfigPayload },
        })
      }

      if (onConfigure && installedCapability) {
        onConfigure(installedCapability)
      } else {
        onClose()
      }
    },
    [
      buildStockConfig,
      capability.id,
      capability.identifier,
      installCapability,
      provider,
      isPending,
      mode,
      onClose,
      onConfigure,
      t,
      updateCapabilityConfig,
    ],
  )

  if (onSubmitHandlerRef) {
    onSubmitHandlerRef.current = handleSubmit
  }

  if (isIntegrationsStatusPending) return <Loading />

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <Label>{t(`capabilities.catalog.${capability.identifier}.config.provider`)}</Label>
        <Select value={provider ?? undefined} onValueChange={setProvider}>
          <SelectTrigger>
            <SelectValue
              placeholder={t(`capabilities.catalog.${capability.identifier}.config.placeholder`)}
            />
          </SelectTrigger>
          <SelectContent>
            {providerOptions.map((option) => (
              <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-3 rounded-lg border bg-background p-4">
        <div className="flex flex-col items-start gap-1">
          <span className="text-base font-medium">
            {t("capabilities.catalog.ProductInformation.config.stockConfig.title")}
          </span>
          <span className="text-xs font-normal text-muted-foreground">
            {t("capabilities.catalog.ProductInformation.config.stockConfig.description")}
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>{t("capabilities.catalog.ProductInformation.config.stockConfig.stockMode")}</Label>
          <Select value={stockMode} onValueChange={setStockMode}>
            <SelectTrigger>
              <SelectValue
                placeholder={t(
                  "capabilities.catalog.ProductInformation.config.stockConfig.stockModePlaceholder",
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {Object.values(StockMode).map((mode) => (
                <SelectItem key={mode} value={mode}>
                  {t(
                    `capabilities.catalog.ProductInformation.config.stockConfig.stockModeOptions.${mode}`,
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            {t(
              `capabilities.catalog.ProductInformation.config.stockConfig.stockModeHints.${stockMode}`,
            )}
          </p>
        </div>

        {stockMode !== "ignore" && (
          <>
            <div className="flex flex-col gap-1.5">
              <Label>
                {t("capabilities.catalog.ProductInformation.config.stockConfig.locationStrategy")}
              </Label>
              <Select value={locationStrategy} onValueChange={setLocationStrategy}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={t(
                      "capabilities.catalog.ProductInformation.config.stockConfig.locationStrategyPlaceholder",
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(LocationStrategy).map((strategy) => (
                    <SelectItem key={strategy} value={strategy}>
                      {t(
                        `capabilities.catalog.ProductInformation.config.stockConfig.locationStrategyOptions.${strategy}`,
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {t(
                  `capabilities.catalog.ProductInformation.config.stockConfig.locationStrategyHints.${locationStrategy}`,
                )}
              </p>
            </div>

            {locationStrategy === "specific" && (
              <div className="flex flex-col gap-1.5">
                <Label>
                  {t(
                    "capabilities.catalog.ProductInformation.config.stockConfig.specificLocations",
                  )}
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between font-normal"
                      disabled={isLoadingLocations}
                    >
                      <span className="truncate text-left">{locationsTriggerLabel}</span>
                      <ChevronDown className="h-5 w-5 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 min-w-56" align="start">
                    <Command>
                      <CommandList>
                        {locations.length === 0 && (
                          <CommandEmpty>
                            {t(
                              "capabilities.catalog.ProductInformation.config.stockConfig.noLocations",
                            )}
                          </CommandEmpty>
                        )}
                        <CommandGroup>
                          {locations.map((location) => {
                            const isSelected = specificLocationIds.includes(location.id)
                            return (
                              <CommandItem
                                key={location.id}
                                value={location.id}
                                onSelect={() => toggleLocation(location.id)}
                                className="gap-2"
                              >
                                <Checkbox checked={isSelected} />
                                <span className="truncate flex-1">
                                  {location.name}
                                  {location.address?.city ? ` — ${location.address.city}` : ""}
                                </span>
                                {location.isFulfillmentService && (
                                  <Badge variant="outline">
                                    {t(
                                      "capabilities.catalog.ProductInformation.config.stockConfig.fulfillmentServiceBadge",
                                    )}
                                  </Badge>
                                )}
                              </CommandItem>
                            )
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {stockMode === "available" && (
              <div className="flex items-center justify-between gap-2 pt-1">
                <Label className="flex flex-col gap-0.5" htmlFor="allow-backorder-switch">
                  <p>
                    {t("capabilities.catalog.ProductInformation.config.stockConfig.allowBackorder")}
                  </p>
                  <p className="text-sm font-normal text-muted-foreground">
                    {t(
                      "capabilities.catalog.ProductInformation.config.stockConfig.allowBackorderDescription",
                    )}
                  </p>
                </Label>
                <Switch
                  checked={allowBackorder}
                  onCheckedChange={setAllowBackorder}
                  id="allow-backorder-switch"
                />
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <Label>
                {t("capabilities.catalog.ProductInformation.config.stockConfig.minQuantity")}
              </Label>
              <Input
                type="number"
                min={1}
                step={1}
                value={minQuantity}
                onChange={(e) => setMinQuantity(e.target.value)}
                placeholder={t(
                  "capabilities.catalog.ProductInformation.config.stockConfig.minQuantityPlaceholder",
                )}
              />
              <p className="text-sm text-muted-foreground">
                {t(
                  "capabilities.catalog.ProductInformation.config.stockConfig.minQuantityDescription",
                )}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
